













































































































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import moment from 'moment';

import { getAntDatePickerLocale } from '@/i18n/dateLocale';
import CollapseCard from '@/components/UI/CollapseCard.vue';

import MmHintedButton from '../../components/MmHintedButton.vue';

import { MM_EMPTY_DASH } from '../../core/format';
import { listDomainEntries, MmBadgeEntry } from '../../core/statuses';
import { MmTask } from '../../types/domain';

import { MmMonitoringBatch } from './batch';
import { createEmptyResultsFilters, MmResultsScreenFilters } from './filters';

const KEY = 'priceMonitoring.mobileMonitoring.results.';

/** Дата без времени и часового пояса: границы суток достраивает сервер */
const DATE_WIRE_FORMAT = 'YYYY-MM-DD';

const DATE_DISPLAY_FORMAT = 'DD.MM.YYYY';

/** Сентинел «Все» в модель не попадает: пустой фильтр и означает «не фильтровать» */
const ALL_TAG = '__mm_all__';

interface MmResultsFilterOption {
	value: string;
	label: string;
	note: string;
	title: string;
}

function isFilledString(value: string | null | undefined): boolean {
	return value !== null && value !== undefined && String(value) !== '';
}

function readText(value: string | null | undefined): string {
	return value === null || value === undefined ? '' : String(value).trim();
}

@Component({
	components: { CollapseCard, MmHintedButton },
})
export default class ResultsFilters extends Vue {
	@Prop({ default: null }) readonly batch!: MmMonitoringBatch | null;

	@Prop({ default: () => createEmptyResultsFilters() }) readonly value!: MmResultsScreenFilters;

	@Prop({ default: false }) readonly busy!: boolean;

	@Prop({ default: '' }) readonly busyReason!: string;

	@Prop({ default: false }) readonly loading!: boolean;

	searchText: string = '';

	draft: MmResultsScreenFilters = createEmptyResultsFilters();

	created(): void {
		this.syncDraftFromValue();
		this.syncSearchText();
	}

	@Watch('value', { deep: true })
	onValueChanged(): void {
		this.syncDraftFromValue();
		this.syncSearchText();
	}

	/** Наблюдатель за пачкой нужен ради поля поиска: неотправленный текст сам не сбросится */
	@Watch('batch')
	onBatchChanged(): void {
		this.syncSearchText();
	}

	/** Сброс фильтров снаружи обязан очистить и само поле ввода */
	@Watch('value.search')
	onSearchChanged(): void {
		this.syncSearchText();
	}

	get model(): MmResultsScreenFilters {
		return this.draft ? this.draft : createEmptyResultsFilters();
	}

	get hasPendingChanges(): boolean {
		const draft = this.buildDraftPayload();

		return !this.filtersEqual(draft, this.value ? this.value : createEmptyResultsFilters());
	}

	get safeTypes(): string[] {
		return this.readList(this.model.types);
	}

	get safeReviewStatuses(): string[] {
		return this.readList(this.model.reviewStatuses);
	}

	get safeOutcomes(): string[] {
		return this.readList(this.model.outcomes);
	}

	get safeTasks(): MmTask[] {
		if (!this.batch || !this.batch.tasks) {
			return [];
		}

		return this.batch.tasks.filter((task) => task != null && isFilledString(task.id));
	}

	get taskOptions(): MmResultsFilterOption[] {
		return this.safeTasks.map((task) => {
			const code = readText(task.competitorCode);
			const store = task.competitorStore;
			const name = store !== null && store !== undefined ? readText(store.name) : '';
			const number = readText(task.displayNumber);

			const label = code !== '' ? code : (number !== '' ? number : MM_EMPTY_DASH);

			return {
				value: String(task.id),
				label: label,
				note: name,
				title: name !== '' ? label + ' — ' + name : label,
			};
		});
	}

	get competitorOptions(): MmResultsFilterOption[] {
		const nameByCode: { [code: string]: string } = {};
		const order: string[] = [];

		const remember = (code: string, name: string): void => {
			if (code === '') {
				return;
			}

			if (order.indexOf(code) === -1) {
				order.push(code);
				nameByCode[code] = name;
				return;
			}

			if (nameByCode[code] === '' && name !== '') {
				nameByCode[code] = name;
			}
		};

		const declared = this.batch && this.batch.competitorCodes ? this.batch.competitorCodes : [];

		declared.forEach((code) => {
			remember(readText(code), '');
		});

		this.safeTasks.forEach((task) => {
			const store = task.competitorStore;

			remember(
				readText(task.competitorCode),
				store !== null && store !== undefined ? readText(store.name) : '',
			);
		});

		return order.map((code) => {
			const name = nameByCode[code];

			return {
				value: code,
				label: code,
				note: name,
				title: name !== '' ? code + ' — ' + name : code,
			};
		});
	}

	get auditorOptions(): MmResultsFilterOption[] {
		const nameByLogin: { [login: string]: string } = {};
		const order: string[] = [];

		this.safeTasks.forEach((task) => {
			const login = readText(task.auditorLogin);

			if (login === '') {
				return;
			}

			const fullName = readText(task.auditorFullName);

			if (order.indexOf(login) === -1) {
				order.push(login);
				nameByLogin[login] = fullName;
				return;
			}

			if (nameByLogin[login] === '' && fullName !== '') {
				nameByLogin[login] = fullName;
			}
		});

		return order.map((login) => {
			const fullName = nameByLogin[login];

			return {
				value: login,
				label: login,
				note: fullName,
				title: fullName !== '' ? login + ' — ' + fullName : login,
			};
		});
	}

	get typeOptions(): MmBadgeEntry[] {
		return listDomainEntries('taskType');
	}

	get reviewOptions(): MmBadgeEntry[] {
		return listDomainEntries('reviewStatus');
	}

	get outcomeOptions(): MmBadgeEntry[] {
		return listDomainEntries('outcome');
	}

	get taskAllValues(): string[] {
		return this.taskOptions.map((option) => option.value);
	}

	get competitorAllValues(): string[] {
		return this.competitorOptions.map((option) => option.value);
	}

	get auditorAllValues(): string[] {
		return this.auditorOptions.map((option) => option.value);
	}

	get typeAllValues(): string[] {
		return this.typeOptions.map((entry) => entry.code);
	}

	get reviewAllValues(): string[] {
		return this.reviewOptions.map((entry) => entry.code);
	}

	get outcomeAllValues(): string[] {
		return this.outcomeOptions.map((entry) => entry.code);
	}

	get hasBatchScope(): boolean {
		return this.safeTasks.length > 0;
	}

	get selectedTaskIds(): string[] {
		return this.hasBatchScope
			? this.keepKnown(this.model.taskIds, this.taskOptions)
			: this.readList(this.model.taskIds);
	}

	get selectedCompetitorCodes(): string[] {
		return this.hasBatchScope
			? this.keepKnown(this.model.competitorCodes, this.competitorOptions)
			: this.readList(this.model.competitorCodes);
	}

	get selectedAuditorLogins(): string[] {
		return this.hasBatchScope
			? this.keepKnown(this.model.auditorLogins, this.auditorOptions)
			: this.readList(this.model.auditorLogins);
	}

	get displayTaskIds(): string[] {
		return this.buildDisplay(this.selectedTaskIds, this.taskAllValues);
	}

	get displayCompetitorCodes(): string[] {
		return this.buildDisplay(this.selectedCompetitorCodes, this.competitorAllValues);
	}

	get displayAuditorLogins(): string[] {
		return this.buildDisplay(this.selectedAuditorLogins, this.auditorAllValues);
	}

	get displayTypes(): string[] {
		return this.buildDisplay(this.safeTypes, this.typeAllValues);
	}

	get displayReviewStatuses(): string[] {
		return this.buildDisplay(this.safeReviewStatuses, this.reviewAllValues);
	}

	get displayOutcomes(): string[] {
		return this.buildDisplay(this.safeOutcomes, this.outcomeAllValues);
	}

	get allPlaceholder(): string {
		return this.$t(KEY + 'filterAll') as string;
	}

	get allLabel(): string {
		return this.allPlaceholder;
	}

	get allTagValue(): string {
		return ALL_TAG;
	}

	get noOptionsText(): string {
		return this.$t('shared.selectBox.noOptions') as string;
	}

	fieldLabelId(field: string): string {
		return 'sm-mobilemon-results-filters-' + field + '-label-' + String((this as any)._uid);
	}

	get busyReasonText(): string {
		const reason = readText(this.busyReason);

		return reason !== ''
			? reason
			: (this.$t('priceMonitoring.mobileMonitoring.states.loading') as string);
	}

	get canReset(): boolean {
		return !this.busy && !this.isDefaultFilters();
	}

	get resetDisabledReason(): string {
		if (this.busy) {
			return this.busyReasonText;
		}

		return '';
	}

	get dateDisplayFormat(): string {
		return DATE_DISPLAY_FORMAT;
	}

	get datePickerLocale(): any {
		return getAntDatePickerLocale();
	}

	/** a-date-picker принимает только объекты moment, наружу же отдаём строки */
	get capturedFromValue(): any {
		const raw = this.model.capturedFrom;

		return isFilledString(raw) ? moment(raw as string, DATE_WIRE_FORMAT) : null;
	}

	get capturedToValue(): any {
		const raw = this.model.capturedTo;

		return isFilledString(raw) ? moment(raw as string, DATE_WIRE_FORMAT) : null;
	}

	disabledCapturedFrom(current: any): boolean {
		const limit = this.capturedToValue;

		if (!current || limit === null) {
			return false;
		}

		return current.isAfter(limit, 'day');
	}

	disabledCapturedTo(current: any): boolean {
		const limit = this.capturedFromValue;

		if (!current || limit === null) {
			return false;
		}

		return current.isBefore(limit, 'day');
	}

	/** ant зовёт эту функцию и игнорирует optionFilterProp из шаблона */
	filterOption(input: string, option: any): boolean {
		const query = input == null ? '' : String(input).trim().toLowerCase();

		if (query === '') {
			return true;
		}

		if (!option || !option.componentOptions || !option.componentOptions.propsData) {
			return false;
		}

		const props = option.componentOptions.propsData;

		if (props.value === ALL_TAG) {
			return true;
		}

		const label = props.label == null ? '' : String(props.label).toLowerCase();
		const title = props.title == null ? '' : String(props.title).toLowerCase();

		return label.indexOf(query) >= 0 || title.indexOf(query) >= 0;
	}

	handleTasksChange(next: any): void {
		this.patchDraft({ taskIds: this.toModelValues(next, this.taskAllValues) });
	}

	handleCompetitorsChange(next: any): void {
		this.patchDraft({ competitorCodes: this.toModelValues(next, this.competitorAllValues) });
	}

	handleAuditorsChange(next: any): void {
		this.patchDraft({ auditorLogins: this.toModelValues(next, this.auditorAllValues) });
	}

	handleTypesChange(next: any): void {
		this.patchDraft({ types: this.toModelValues(next, this.typeAllValues) });
	}

	handleReviewStatusesChange(next: any): void {
		this.patchDraft({ reviewStatuses: this.toModelValues(next, this.reviewAllValues) });
	}

	handleOutcomesChange(next: any): void {
		this.patchDraft({ outcomes: this.toModelValues(next, this.outcomeAllValues) });
	}

	/** Второй аргумент — строка в формате показа; для сервера форматируем сами из объекта moment */
	handleCapturedFromChange(date: any): void {
		this.patchDraft({ capturedFrom: date ? date.format(DATE_WIRE_FORMAT) : null });
	}

	handleCapturedToChange(date: any): void {
		this.patchDraft({ capturedTo: date ? date.format(DATE_WIRE_FORMAT) : null });
	}

	handleSearchInput(event: any): void {
		this.searchText = event && event.target ? String(event.target.value) : '';
	}

	/** Значение берём из аргумента: при очистке крестиком ant 1.7.8 шлёт search раньше change */
	handleSearchSubmit(value: any): void {
		const raw = value === null || value === undefined ? this.searchText : String(value);
		const next = raw.trim();

		this.searchText = raw;

		if (next === readText(this.model.search)) {
			return;
		}

		this.patchDraft({ search: next });
	}

	handleReset(): void {
		this.searchText = '';
		this.$emit('reset');
	}

	handleApply(): void {
		if (this.busy || this.loading) {
			return;
		}

		this.$emit('apply', this.buildDraftPayload());
	}

	/** Копируем все поля исходного объекта, а не поимённо: новое поле фильтров не должно теряться */
	private patchDraft(patch: { [key: string]: any }): void {
		const base = this.model;
		const next: any = {};

		Object.keys(base).forEach((field) => {
			const raw = (base as any)[field];

			next[field] = Array.isArray(raw) ? raw.slice() : raw;
		});

		next.taskIds = this.selectedTaskIds;
		next.competitorCodes = this.selectedCompetitorCodes;
		next.auditorLogins = this.selectedAuditorLogins;

		Object.keys(patch).forEach((field) => {
			next[field] = patch[field];
		});

		this.draft = next as MmResultsScreenFilters;
	}

	private syncDraftFromValue(): void {
		this.draft = this.cloneFilters(this.value ? this.value : createEmptyResultsFilters());
	}

	private cloneFilters(source: MmResultsScreenFilters): MmResultsScreenFilters {
		const next: any = {};

		Object.keys(source).forEach((field) => {
			const raw = (source as any)[field];

			next[field] = Array.isArray(raw) ? raw.slice() : raw;
		});

		return next as MmResultsScreenFilters;
	}

	private buildDraftPayload(): MmResultsScreenFilters {
		const next = this.cloneFilters(this.model);

		next.search = readText(this.searchText);

		return next;
	}

	private filtersEqual(left: MmResultsScreenFilters, right: MmResultsScreenFilters): boolean {
		return this.sameList(left.taskIds, right.taskIds)
			&& this.sameList(left.competitorCodes, right.competitorCodes)
			&& this.sameList(left.auditorLogins, right.auditorLogins)
			&& this.sameList(left.types, right.types)
			&& this.sameList(left.reviewStatuses, right.reviewStatuses)
			&& this.sameList(left.outcomes, right.outcomes)
			&& left.capturedFrom === right.capturedFrom
			&& left.capturedTo === right.capturedTo
			&& readText(left.search) === readText(right.search);
	}

	private syncSearchText(): void {
		this.searchText = readText(this.model.search);
	}

	private readList(list: string[] | null | undefined): string[] {
		return list && list.length > 0 ? list.slice() : [];
	}

	private toStringList(next: any): string[] {
		return Array.isArray(next) ? next.map((item: any) => String(item)) : [];
	}

	private keepKnown(list: string[] | null | undefined, options: MmResultsFilterOption[]): string[] {
		const source = this.readList(list);

		if (source.length === 0) {
			return [];
		}

		const known: { [value: string]: boolean } = {};

		options.forEach((option) => {
			known[option.value] = true;
		});

		return source.filter((item) => known[String(item)] === true);
	}

	private buildDisplay(selected: string[] | null | undefined, allValues: string[]): string[] {
		const real = this.readList(selected);

		if (real.length === 0) {
			return [ALL_TAG];
		}

		if (allValues.length > 0 && this.coversAll(real, allValues)) {
			return [ALL_TAG];
		}

		return real;
	}

	private coversAll(picked: string[], allValues: string[]): boolean {
		for (let i = 0; i < allValues.length; i += 1) {
			if (picked.indexOf(allValues[i]) === -1) {
				return false;
			}
		}

		return true;
	}

	private toModelValues(next: any, allValues: string[]): string[] {
		const picked = this.toStringList(next);

		if (picked.length === 0) {
			return [];
		}

		const last = picked[picked.length - 1];

		if (last === ALL_TAG) {
			return [];
		}

		const real: string[] = [];

		for (let i = 0; i < picked.length; i += 1) {
			if (picked[i] !== ALL_TAG) {
				real.push(picked[i]);
			}
		}

		if (real.length === 0) {
			return [];
		}

		if (allValues.length > 0 && this.coversAll(real, allValues)) {
			return [];
		}

		return real;
	}

	private isDefaultFilters(): boolean {
		const current = this.model;
		const def = createEmptyResultsFilters();

		return this.sameList(current.taskIds, def.taskIds)
			&& this.sameList(current.competitorCodes, def.competitorCodes)
			&& this.sameList(current.auditorLogins, def.auditorLogins)
			&& this.sameList(current.types, def.types)
			&& this.sameList(current.reviewStatuses, def.reviewStatuses)
			&& this.sameList(current.outcomes, def.outcomes)
			&& current.capturedFrom === def.capturedFrom
			&& current.capturedTo === def.capturedTo
			&& current.search === def.search;
	}

	private sameList(left: string[] | null | undefined, right: string[]): boolean {
		const a = this.readList(left);
		const b = this.readList(right);

		if (a.length !== b.length) {
			return false;
		}

		for (let i = 0; i < a.length; i += 1) {
			if (a[i] !== b[i]) {
				return false;
			}
		}

		return true;
	}
}
